import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { dashboardAPI } from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import Tabs from '../components/ui/Tabs';
import HelpTooltip from '../components/ui/HelpTooltip';
import { usePageTour } from '../components/onboarding/PageTour';
import { formatDate } from '../utils/dateFormat';

const StatusPill = ({ status, daysUntilDue }) => {
  const { t } = useTranslation();
  if (!status) return <span className="text-slate-400 dark:text-slate-400 text-sm">—</span>;

  const map = {
    green: {
      bg: 'bg-emerald-100 dark:bg-slate-800',
      text: 'text-emerald-800 dark:text-emerald-300',
      border: 'border-emerald-200 dark:border-slate-700',
      label: t('ipDashboard.pillUpToDate'),
    },
    yellow: {
      bg: 'bg-amber-100 dark:bg-slate-800',
      text: 'text-amber-800 dark:text-amber-300',
      border: 'border-amber-200 dark:border-slate-700',
      label: t('ipDashboard.pillSoon'),
    },
    red: {
      bg: 'bg-red-100 dark:bg-slate-800',
      text: 'text-red-800 dark:text-red-300',
      border: 'border-red-200 dark:border-slate-700',
      label: t('ipDashboard.pillExpired'),
    },
  };
  const cfg = map[status];

  let suffix = '';
  if (daysUntilDue === null || daysUntilDue === undefined) {
    suffix = ` • ${t('ipDashboard.pillNotTrained')}`;
  } else if (status === 'red') {
    suffix = ` • ${t('ipDashboard.daysOverdue', { count: Math.abs(daysUntilDue) })}`;
  } else {
    suffix = ` • ${t('ipDashboard.daysUntil', { count: daysUntilDue })}`;
  }

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.bg} ${cfg.text} ${cfg.border}`}
    >
      {cfg.label}
      {suffix}
    </span>
  );
};

const SemaforCard = ({ label, value, status, sublabel }) => {
  const map = {
    green: {
      bg: 'bg-emerald-50 dark:bg-slate-800/60',
      text: 'text-emerald-700 dark:text-emerald-300',
      dot: 'bg-emerald-500',
      border: 'border-emerald-200 dark:border-slate-700',
    },
    yellow: {
      bg: 'bg-amber-50 dark:bg-slate-800/60',
      text: 'text-amber-700 dark:text-amber-300',
      dot: 'bg-amber-500',
      border: 'border-amber-200 dark:border-slate-700',
    },
    red: {
      bg: 'bg-red-50 dark:bg-slate-800/60',
      text: 'text-red-700 dark:text-red-300',
      dot: 'bg-red-500',
      border: 'border-red-200 dark:border-slate-700',
    },
    total: {
      bg: 'bg-slate-50 dark:bg-slate-800',
      text: 'text-slate-700 dark:text-slate-300',
      dot: 'bg-slate-400',
      border: 'border-slate-200 dark:border-slate-700',
    },
  };
  const cfg = map[status] || map.total;

  return (
    <div className={`rounded-xl border ${cfg.border} ${cfg.bg} p-4`}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`}></span>
        <p className={`text-sm font-medium ${cfg.text}`}>{label}</p>
      </div>
      <p className={`text-3xl font-semibold ${cfg.text} tabular-nums`}>{value}</p>
      {sublabel && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{sublabel}</p>}
    </div>
  );
};

const IPDashboard = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all | ssm | su
  const [statusTab, setStatusTab] = useState('all'); // all | red | yellow | green

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await dashboardAPI.getIPSemaforizare();
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || t('ipDashboard.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const counts = useMemo(() => {
    if (!data) return null;
    if (filter === 'ssm') return data.summary.ssm;
    if (filter === 'su') return data.summary.su;
    return data.summary;
  }, [data, filter]);

  const filteredEmployees = useMemo(() => {
    if (!data) return [];
    let rows = data.employees;

    if (filter === 'ssm') {
      rows = rows.filter((e) => e.ipSSM).map((e) => ({ ...e, _statusToShow: e.ipSSM.status }));
    } else if (filter === 'su') {
      rows = rows.filter((e) => e.ipSU).map((e) => ({ ...e, _statusToShow: e.ipSU.status }));
    } else {
      rows = rows.map((e) => ({ ...e, _statusToShow: e.overallStatus }));
    }

    if (statusTab !== 'all') {
      rows = rows.filter((e) => e._statusToShow === statusTab);
    }

    return rows;
  }, [data, filter, statusTab]);

  // Tour contextual pentru IP Dashboard — chemat ÎNAINTE de orice early-return ca să respecte
  // regulile React (hook-urile trebuie chemate în aceeași ordine la fiecare render).
  const ipTourSteps = [
    {
      selector: '[data-tour="ip-summary"]',
      titleKey: 'onboarding.tour.ip.summary.title',
      descKey: 'onboarding.tour.ip.summary.desc',
      placement: 'bottom',
    },
    {
      selector: '[data-tour="ip-table"]',
      titleKey: 'onboarding.tour.ip.table.title',
      descKey: 'onboarding.tour.ip.table.desc',
      placement: 'top',
    },
  ];
  const { tour: ipTour, startTour: startIpTour } = usePageTour({
    tourId: 'ip-dashboard',
    steps: ipTourSteps,
  });

  if (loading) return <Spinner size="lg" label={t('ipDashboard.loadingSpinner')} />;
  if (error) return <Alert type="error">{error}</Alert>;
  if (!data) return null;

  const { summary, byCompany, byDepartment } = data;

  // Detectăm ce servicii au date — ascundem cele fără date complet
  const hasSSMData = summary.ssm.total > 0;
  const hasSUData = summary.su.total > 0;
  const hasBothServices = hasSSMData && hasSUData;

  return (
    <div className="space-y-6">
      {ipTour}
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            {t('ipDashboard.title')}
            <HelpTooltip section="ip-dashboard" text={t('ipDashboard.helpTooltip')} />
          </span>
        }
        subtitle={t('ipDashboard.subtitle')}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" size="sm" onClick={startIpTour}>
              {t('onboarding.replay.btn')}
            </Button>
          </div>
        }
      />

      {/* Explicație legătura cu notificări */}
      <Alert type="info">
        <div className="text-sm">
          <p className="font-medium">{t('ipDashboard.howNotificationsWork')}</p>
          <p className="text-slate-600 dark:text-slate-300 mt-0.5">
            <Trans i18nKey="ipDashboard.howNotificationsDesc" components={{ 1: <strong /> }} />
          </p>
        </div>
      </Alert>

      {/* Filtru SSM / SU / Ambele — afișat doar dacă ambele servicii au date */}
      {hasBothServices && (
        <div className="flex gap-2">
          {[
            { id: 'all', label: t('ipDashboard.filterBoth') },
            { id: 'ssm', label: t('ipDashboard.filterSSMOnly') },
            { id: 'su', label: t('ipDashboard.filterSUOnly') },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => setFilter(opt.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === opt.id
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Carduri semaforizare */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-tour="ip-summary">
        <SemaforCard
          label={t('ipDashboard.totalEmployees')}
          value={counts.total}
          status="total"
          sublabel={t('ipDashboard.withIPConfigured')}
        />
        <SemaforCard
          label={t('ipDashboard.statOnTime')}
          value={counts.green}
          status="green"
          sublabel={t('ipDashboard.onTimeSub', { days: data.thresholds.yellowDays })}
        />
        <SemaforCard
          label={t('ipDashboard.statSoon')}
          value={counts.yellow}
          status="yellow"
          sublabel={t('ipDashboard.soonSub', { days: data.thresholds.yellowDays })}
        />
        <SemaforCard
          label={t('ipDashboard.statExpired')}
          value={counts.red}
          status="red"
          sublabel={t('ipDashboard.urgentAction')}
        />
      </div>

      {/* Per firmă (doar dacă sunt mai multe firme) */}
      {byCompany.length > 1 && (
        <Card>
          <div className="mb-3">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {t('ipDashboard.byCompany')}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                  <th className="py-2 pr-4 font-medium">{t('ipDashboard.colCompany')}</th>
                  <th className="py-2 px-3 font-medium text-center">{t('ipDashboard.colTotal')}</th>
                  <th className="py-2 px-3 font-medium text-center text-emerald-700 dark:text-emerald-300">
                    {t('ipDashboard.colOnTime')}
                  </th>
                  <th className="py-2 px-3 font-medium text-center text-amber-700 dark:text-amber-300">
                    {t('ipDashboard.colSoon')}
                  </th>
                  <th className="py-2 px-3 font-medium text-center text-red-700 dark:text-red-300">
                    {t('ipDashboard.colExpired')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {byCompany.map((c) => (
                  <tr
                    key={c.companyId}
                    className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <td className="py-2 pr-4 font-medium text-slate-900 dark:text-slate-100">
                      {c.companyName}
                    </td>
                    <td className="py-2 px-3 text-center tabular-nums">{c.total}</td>
                    <td className="py-2 px-3 text-center tabular-nums text-emerald-700 dark:text-emerald-300">
                      {c.green}
                    </td>
                    <td className="py-2 px-3 text-center tabular-nums text-amber-700 dark:text-amber-300">
                      {c.yellow}
                    </td>
                    <td className="py-2 px-3 text-center tabular-nums text-red-700 dark:text-red-300 font-semibold">
                      {c.red}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Per departament (doar dacă sunt date) */}
      {byDepartment.length > 0 && (
        <Card>
          <div className="mb-3">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {t('ipDashboard.byDepartment')}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                  <th className="py-2 pr-4 font-medium">{t('ipDashboard.colDept')}</th>
                  {byCompany.length > 1 && (
                    <th className="py-2 pr-4 font-medium">{t('ipDashboard.colCompany')}</th>
                  )}
                  <th className="py-2 px-3 font-medium text-center">{t('ipDashboard.colTotal')}</th>
                  <th className="py-2 px-3 font-medium text-center text-emerald-700 dark:text-emerald-300">
                    {t('ipDashboard.colOnTime')}
                  </th>
                  <th className="py-2 px-3 font-medium text-center text-amber-700 dark:text-amber-300">
                    {t('ipDashboard.colSoon')}
                  </th>
                  <th className="py-2 px-3 font-medium text-center text-red-700 dark:text-red-300">
                    {t('ipDashboard.colExpired')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {byDepartment.map((d) => (
                  <tr
                    key={d.departmentId}
                    className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <td className="py-2 pr-4 font-medium text-slate-900 dark:text-slate-100">
                      {d.departmentName}
                    </td>
                    {byCompany.length > 1 && (
                      <td className="py-2 pr-4 text-slate-600 dark:text-slate-300">
                        {d.companyName}
                      </td>
                    )}
                    <td className="py-2 px-3 text-center tabular-nums">{d.total}</td>
                    <td className="py-2 px-3 text-center tabular-nums text-emerald-700 dark:text-emerald-300">
                      {d.green}
                    </td>
                    <td className="py-2 px-3 text-center tabular-nums text-amber-700 dark:text-amber-300">
                      {d.yellow}
                    </td>
                    <td className="py-2 px-3 text-center tabular-nums text-red-700 dark:text-red-300 font-semibold">
                      {d.red}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Tab-uri pe statusuri pentru lista detaliată */}
      <Card padding={false} data-tour="ip-table">
        <div className="px-4 pt-3">
          <Tabs
            activeTab={statusTab}
            onChange={setStatusTab}
            tabs={[
              { id: 'all', label: t('ipDashboard.filterAll'), count: counts.total },
              { id: 'red', label: t('ipDashboard.filterExpired'), count: counts.red },
              { id: 'yellow', label: t('ipDashboard.filterSoon'), count: counts.yellow },
              { id: 'green', label: t('ipDashboard.filterOnTime'), count: counts.green },
            ]}
          />
        </div>

        {filteredEmployees.length === 0 ? (
          <div className="p-6">
            <EmptyState title={t('ipDashboard.noEmployeesInCategory')} />
          </div>
        ) : (
          <>
            {/* MOBILE: card per angajat (sub md). Pe ecran mic tabelul cu 5-6 coloane se citește greu. */}
            <div className="md:hidden divide-y divide-slate-100 dark:divide-slate-800">
              {filteredEmployees.map((emp) => {
                const needsAction = emp.overallStatus === 'red' || emp.overallStatus === 'yellow';
                return (
                  <div key={`mob-${emp.id}`} className="p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <Link
                          to={`/employees/${emp.id}`}
                          className="block text-sm font-semibold text-slate-900 dark:text-slate-100 hover:text-primary-600 truncate"
                        >
                          {emp.employeeName}
                        </Link>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {emp.email}
                        </p>
                        <p className="text-xs text-slate-700 dark:text-slate-300 mt-0.5">
                          {emp.jobTitle || '—'}
                        </p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                          {emp.companyName}
                          {emp.departmentName ? ` · ${emp.departmentName}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {hasSSMData && (filter === 'all' || filter === 'ssm') && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-bold uppercase text-slate-500 dark:text-slate-400">
                            SSM
                          </span>
                          <StatusPill
                            status={emp.ipSSM?.status}
                            daysUntilDue={emp.ipSSM?.daysUntilDue}
                          />
                        </div>
                      )}
                      {hasSUData && (filter === 'all' || filter === 'su') && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-bold uppercase text-slate-500 dark:text-slate-400">
                            SU
                          </span>
                          <StatusPill
                            status={emp.ipSU?.status}
                            daysUntilDue={emp.ipSU?.daysUntilDue}
                          />
                        </div>
                      )}
                    </div>
                    <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                      {needsAction ? (
                        <Link
                          to={`/assign-courses?employeeId=${emp.id}&companyId=${emp.companyId}`}
                        >
                          <Button variant="primary" size="sm">
                            {t('ipDashboard.sendReminder')}
                          </Button>
                        </Link>
                      ) : (
                        <Link
                          to={`/employees/${emp.id}`}
                          className="text-xs text-slate-500 dark:text-slate-400 hover:text-primary-600"
                        >
                          {t('common.details')} →
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* DESKTOP: tabelul existent — vizibil de la md+ */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800">
                  <tr className="text-left text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                    <th className="py-3 px-4 font-medium">{t('ipDashboard.columns.employee')}</th>
                    <th className="py-3 px-4 font-medium">{t('ipDashboard.columns.jobTitle')}</th>
                    <th className="py-3 px-4 font-medium">
                      {t('ipDashboard.columns.companyDept')}
                    </th>
                    {hasSSMData && (filter === 'all' || filter === 'ssm') && (
                      <th className="py-3 px-4 font-medium">{t('ipDashboard.columns.ipSSM')}</th>
                    )}
                    {hasSUData && (filter === 'all' || filter === 'su') && (
                      <th className="py-3 px-4 font-medium">{t('ipDashboard.columns.ipSU')}</th>
                    )}
                    <th className="py-3 px-4 font-medium text-right">
                      {t('ipDashboard.columns.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map((emp) => {
                    const needsAction =
                      emp.overallStatus === 'red' || emp.overallStatus === 'yellow';

                    return (
                      <tr
                        key={emp.id}
                        className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"
                      >
                        <td className="py-3 px-4">
                          <Link
                            to={`/employees/${emp.id}`}
                            className="font-medium text-slate-900 dark:text-slate-100 hover:text-primary-600"
                          >
                            {emp.employeeName}
                          </Link>
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            {emp.email}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-700 dark:text-slate-300">
                          {emp.jobTitle || '—'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-slate-900 dark:text-slate-100">
                            {emp.companyName}
                          </div>
                          {emp.departmentName && (
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              {emp.departmentName}
                            </div>
                          )}
                        </td>
                        {hasSSMData && (filter === 'all' || filter === 'ssm') && (
                          <td className="py-3 px-4">
                            <StatusPill
                              status={emp.ipSSM?.status}
                              daysUntilDue={emp.ipSSM?.daysUntilDue}
                            />
                            {emp.ipSSM?.dueDate && (
                              <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                {formatDate(emp.ipSSM.dueDate)}
                              </div>
                            )}
                          </td>
                        )}
                        {hasSUData && (filter === 'all' || filter === 'su') && (
                          <td className="py-3 px-4">
                            <StatusPill
                              status={emp.ipSU?.status}
                              daysUntilDue={emp.ipSU?.daysUntilDue}
                            />
                            {emp.ipSU?.dueDate && (
                              <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                {formatDate(emp.ipSU.dueDate)}
                              </div>
                            )}
                          </td>
                        )}
                        <td className="py-3 px-4 text-right">
                          {needsAction ? (
                            <Link
                              to={`/assign-courses?employeeId=${emp.id}&companyId=${emp.companyId}`}
                            >
                              <Button variant="primary" size="sm">
                                {t('ipDashboard.sendReminder')}
                              </Button>
                            </Link>
                          ) : (
                            <Link
                              to={`/employees/${emp.id}`}
                              className="text-xs text-slate-500 dark:text-slate-400 hover:text-primary-600"
                            >
                              {t('common.details')} →
                            </Link>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default IPDashboard;
